/**
 * @file src/room/authIdentity.js
 * @description The logged-in room identity: resolves the viewer's platform account ONCE per page
 * (bounded) so the room register can carry their real display name and userId. Guests — and any
 * resolution that fails or outlives the bound — yield nulls, and the register falls back to the
 * generated handle exactly as before.
 *
 * Why a one-shot that can only IMPROVE: the room-visible name must be STABLE for the tab's life,
 * so the first resolution is frozen and reused by every register until an explicit reset (logout).
 * The exception is a fallback that turns out to be wrong — the register already went out under the
 * generated handle, and a logged-in member would otherwise be stuck with it forever. So a better
 * identity landing later replaces the frozen one and notifies `onAuthRoomIdentityUpgrade`
 * subscribers, who re-register — the relay re-runs its name disambiguation for a changed base
 * name, so peers see the real name instead of a stale handle. `rank` defines "better", and the
 * rule is one-directional: nothing can ever take away what is already showing.
 *
 * Several things can land that late identity, and all of them must be able to: the bounded lookup
 * settling after the timer won the race, and a host auth token arriving after boot
 * (`orbiters:auth-token`, which every other subsystem already re-resolves on). Without the second,
 * an outcome that settled as a guest was permanent for the life of the page — the upgrade was
 * subscribed to the very promise that had just settled guest, so nothing could change its mind.
 * Because those writers race each other, they all go through `settleIdentity`: whichever arrives
 * first seeds, later ones may only improve, and anything from before a reset is discarded.
 *
 * Auth seam: the viewer's own profile carries the platform display name / username — the Firebase
 * user record carries neither (custom tokens are minted from the uid alone). The profile call is
 * authenticated by the SESSION COOKIE, which every request already sends and the endpoint already
 * verifies. A bearer token is attached only when one happens to exist: that is the third-party
 * embed case, where the orbiter runs in a genuinely cross-site iframe and the cookie never reaches
 * the API. Requiring the bearer for everyone made a same-site boot depend on a token mint it did
 * not need, and any hiccup in that mint erased the viewer's name and avatar for the whole page.
 */

import { resolveViewerAuth } from '../auth/viewerAuth.js';
import { fetchJsonFromApi } from '../api/httpClient.js';

/** How long the first register may wait for the account resolution before going out as guest. */
const AUTH_RESOLVE_TIMEOUT_MS = 3000;

const GUEST = Object.freeze({ displayName: null, userId: null });

let resolved = null; // frozen outcome: { displayName: string|null, userId: string|null } | null
let pending = null; // in-flight resolution promise (deduped)
let upgradeListeners = []; // notified whenever a late result IMPROVES the frozen outcome
let detachAuthTokenRetry = null; // removes the `orbiters:auth-token` retry listener, if attached
let retryInFlight = false; // one retry lookup at a time (hosts can announce a token twice)
let retryAgain = false; // an announcement arrived mid-retry: re-run once rather than lose it
let generation = 0; // bumped by every reset; a result from an older generation is discarded

function firstNonEmpty(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return null;
}

/**
 * How much of an identity this is: 0 a guest, 1 an account id but no name yet (the avatar can
 * load, the name cannot), 2 complete. Three states genuinely exist — a profile call that fails
 * while the auth stack DID produce a uid lands on 1 — so ordering them is what lets one rule
 * cover every writer: the outcome may only move UP this scale. That is what stops a slow lookup,
 * a late token, or the bound's own guest fallback from taking away something better that already
 * landed, and it keeps a name-less id improvable instead of freezing it forever.
 */
function rank(identity) {
  if (!identity) return 0;
  if (identity.displayName !== null) return 2;
  return identity.userId !== null ? 1 : 0;
}

/**
 * Install a resolution. Whichever result arrives first seeds the frozen outcome; every later one
 * may only improve it, and improving it notifies subscribers so they re-register under the better
 * name. A result from before a reset is dropped — logging out must not be undone by a request it
 * happened to race.
 */
function settleIdentity(identity, forGeneration) {
  if (forGeneration !== generation) return;
  if (resolved !== null && rank(identity) <= rank(resolved)) return;
  const isUpgrade = resolved !== null;
  resolved = identity;
  if (!isUpgrade) return; // the first outcome is what the register was waiting for, not an upgrade
  // Subscribers stay subscribed: `rank` is strictly increasing and capped, so this fires at most
  // twice in a page's life (guest → id, id → named), and a re-register is idempotent. Dropping
  // them after the first call would silence exactly the step that finally carries the NAME.
  for (const listener of upgradeListeners) {
    try { listener(identity); } catch { /* subscriber's problem */ }
  }
}

/** The frozen account identity, or null while the first resolution is still in flight. */
export function getAuthRoomIdentity() {
  return resolved;
}

/**
 * Resolve the viewer's account identity for room registration, bounded by `timeoutMs`. One
 * resolution per page: later calls return the first outcome, and a lookup that loses the race can
 * only ever IMPROVE it, never replace it with less. Collaborators are injectable for tests.
 * @param {{ timeoutMs?: number, resolveAuth?: typeof resolveViewerAuth,
 *   fetchApi?: typeof fetchJsonFromApi }} [options]
 * @returns {Promise<{ displayName: string|null, userId: string|null }>}
 */
export function resolveAuthRoomIdentity({
  timeoutMs = AUTH_RESOLVE_TIMEOUT_MS,
  resolveAuth = resolveViewerAuth,
  fetchApi = fetchJsonFromApi,
} = {}) {
  if (pending) return pending;
  const forGeneration = generation;
  const collaborators = { resolveAuth, fetchApi };
  listenForLateAuthToken(collaborators);
  pending = (async () => {
    let timer = null;
    const lookup = lookupAccountIdentity(collaborators).catch(() => GUEST);
    const outcome = await Promise.race([
      lookup,
      new Promise((resolve) => {
        timer = setTimeout(() => resolve(GUEST), timeoutMs);
      }),
    ]);
    if (timer !== null) clearTimeout(timer);
    settleIdentity(outcome, forGeneration);
    // The bound may have won the race, in which case the lookup is still running and can land a
    // better identity after the register already went out as guest.
    void lookup.then((late) => settleIdentity(late, forGeneration));
    return resolved;
  })();
  return pending;
}

/**
 * A guest outcome is not the end of the story: an embedding host delivers its auth token AFTER
 * boot, and a same-site session can be established after the room is already joined. Every other
 * subsystem re-resolves on `orbiters:auth-token`; the room does too.
 */
function listenForLateAuthToken(collaborators) {
  if (detachAuthTokenRetry || typeof document === 'undefined') return;
  const onAuthToken = () => retryLookup(collaborators);
  document.addEventListener('orbiters:auth-token', onAuthToken);
  detachAuthTokenRetry = () => document.removeEventListener('orbiters:auth-token', onAuthToken);
}

/**
 * Re-run the lookup for auth that arrived after boot. At most one request is in flight, but an
 * announcement that lands during one is COALESCED into a single re-run rather than dropped: a host
 * announces on both of its delivery paths, and the second is often the one that would have won.
 */
function retryLookup(collaborators) {
  if (rank(resolved) === 2) return; // nothing left to improve
  if (retryInFlight) { retryAgain = true; return; }
  retryInFlight = true;
  const forGeneration = generation;
  void lookupAccountIdentity(collaborators)
    .then((identity) => settleIdentity(identity, forGeneration))
    .catch(() => { /* still a guest as far as the room is concerned */ })
    .finally(() => {
      retryInFlight = false;
      if (!retryAgain) return;
      retryAgain = false;
      retryLookup(collaborators);
    });
}

/**
 * Subscribe to a late improvement of the frozen identity (see the header) — a better identity
 * landing after the first register already went out. Fires at most twice in a page's life, and
 * never for the first resolution itself. Returns an unsubscribe.
 * @param {(identity: { displayName: string|null, userId: string|null }) => void} listener
 */
export function onAuthRoomIdentityUpgrade(listener) {
  upgradeListeners.push(listener);
  return () => {
    upgradeListeners = upgradeListeners.filter((entry) => entry !== listener);
  };
}

/**
 * The account lookup: own profile → name + id. The request always goes out — it carries the
 * session cookie, which is what authenticates a same-site viewer, and a signed-out visitor is
 * simply answered with a 401 and stays a guest. A bearer is attached when the auth stack produced
 * one (the cross-site embed case); failing to produce one is not a reason to skip the call.
 */
async function lookupAccountIdentity({ resolveAuth, fetchApi }) {
  const auth = await resolveAuth().catch(() => null);
  const response = await fetchApi('/me/users/profile', { authToken: auth?.authToken ?? null });
  if (!response?.ok) return { displayName: null, userId: auth?.userId ?? null };
  const profile = await response.json().catch(() => null);
  return {
    // Platform convention (same as entity owners elsewhere): display name first, else username.
    displayName: firstNonEmpty(profile?.displayName, profile?.username),
    userId: firstNonEmpty(profile?.userId) ?? auth?.userId ?? null,
  };
}

/**
 * Drop the frozen outcome so the next resolution re-runs (explicit logout, tests). Bumping the
 * generation is what makes this stick: requests already in flight resolve into a generation that
 * no longer exists and are discarded, so a logout cannot be undone by the lookup it raced.
 */
export function resetAuthRoomIdentity() {
  generation += 1;
  resolved = null;
  pending = null;
  upgradeListeners = [];
  retryInFlight = false;
  retryAgain = false;
  detachAuthTokenRetry?.();
  detachAuthTokenRetry = null;
}
